import { FC, useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { fileService } from '@services/file-service';
import { UploadedFile } from '@/types/index';

interface PhotoUploadZoneProps {
  propertyId: string;
  photos: UploadedFile[];
  onUploadSuccess: (file: UploadedFile) => void;
  maxPhotos?: number;
}

export const PhotoUploadZone: FC<PhotoUploadZoneProps> = ({
  propertyId,
  photos,
  onUploadSuccess,
  maxPhotos = 10,
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (photos.length >= maxPhotos) {
      alert(`Maximum ${maxPhotos} photos atteint`);
      return;
    }

    const remainingSlots = maxPhotos - photos.length;
    const filesToUpload = acceptedFiles.slice(0, remainingSlots);

    setUploading(true);

    for (let i = 0; i < filesToUpload.length; i++) {
      try {
        const uploadedFile = await fileService.uploadFile(propertyId, filesToUpload[i]);
        onUploadSuccess(uploadedFile);
        setProgress(((i + 1) / filesToUpload.length) * 100);
      } catch (error) {
        console.error('Erreur upload:', error);
        alert(`Erreur lors de l'upload de ${filesToUpload[i].name}`);
      }
    }

    setUploading(false);
    setProgress(0);
  }, [propertyId, photos.length, maxPhotos, onUploadSuccess]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
    },
    multiple: true,
    disabled: uploading || photos.length >= maxPhotos,
  });

  return (
    <div
      {...getRootProps()}
      className={`
        border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
        transition-colors duration-200
        ${isDragActive
          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
          : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'
        }
        ${photos.length >= maxPhotos ? 'opacity-50 cursor-not-allowed' : ''}
      `}
    >
      <input {...getInputProps()} />

      {uploading ? (
        <div>
          <p className="text-gray-600 dark:text-gray-400 mb-2">
            Upload en cours... {Math.round(progress)}%
          </p>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      ) : (
        <>
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {isDragActive
              ? 'Déposez les images ici...'
              : 'Glissez-déposez des images ou cliquez pour sélectionner'
            }
          </p>
          <p className="mt-1 text-xs text-gray-500">
            JPEG, PNG, WebP - {photos.length}/{maxPhotos} photos
          </p>
        </>
      )}
    </div>
  );
};
